import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Wallet, Loader2, AlertCircle } from "lucide-react";

export default function Login() {
  const { user, loading, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!loading && user) {
    return <Navigate to={location.state?.from?.pathname || "/"} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(username, password);
      navigate("/", { replace: true });
    } catch (err) {
      setError(apiError(err, "Login gagal. Periksa username dan password."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2 bg-white" data-testid="login-page">
      <div className="flex items-center justify-center px-6 py-12 sm:px-12">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-10">
            <div className="grid h-12 w-12 place-items-center rounded-2xl bg-slate-900">
              <Wallet className="h-6 w-6 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight text-slate-900">SaldoPro 2026</h1>
              <p className="text-sm text-slate-500">Laporan Posisi Saldo Harian</p>
            </div>
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-slate-900 mb-2">Masuk ke Akun</h2>
          <p className="text-sm text-slate-500 mb-8">
            Gunakan username dan password yang diberikan admin untuk entry data saldo.
          </p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {error && (
              <div
                className="flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700"
                data-testid="login-error-message"
              >
                <AlertCircle className="h-4 w-4 shrink-0" /> {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                data-testid="login-username-input"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="contoh: asroel005@gmail.com"
                autoComplete="username"
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                required
                className="h-11"
              />
            </div>
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={submitting}
              className="h-11 w-full bg-slate-900 text-white hover:bg-emerald-700 transition-colors"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Memproses...
                </>
              ) : (
                "Masuk"
              )}
            </Button>
          </form>
        </div>
      </div>

      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBkYXNoYm9hcmQlMjBncmFwaCUyMGFjY291bnRpbmd8ZW58MHx8fHwxNzg0ODc3MDQ1fDA&ixlib=rb-4.1.0&q=85"
          alt="Dashboard keuangan"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/80" />
        <div className="relative flex h-full flex-col justify-end p-12">
          <p className="text-3xl font-bold leading-snug text-white tracking-tight">
            Catat posisi saldo setiap hari, pantau trennya dalam grafik.
          </p>
          <p className="mt-4 text-sm text-slate-300 max-w-md">
            Setiap entry tercatat otomatis dengan tanggal dan jam. Laporan dapat diunduh dalam format Excel dan PDF.
          </p>
        </div>
      </div>
    </div>
  );
}
